import * as THREE from "three";

export class EnergyLine {
  constructor(params = {}) {
    this.params = {
      radius0: 0.1, // Starting radius (much smaller)
      radius1: 0.1, // Ending radius
      minRadius: 0.05, // Minimum radius
      maxIterations: 7, // Number of line segments
      roughness: 0.85, // How jagged the line is
      straightness: 0.6, // How straight the overall path is
      timeScale: 0.7, // Animation speed
      intensity: 1, // Energy intensity (increases with more connections)
      color: new THREE.Color(0xb0ffff),
      ...params,
    };

    this.geometry = null;
    this.material = null;
    this.mesh = null;
    this.points = [];
    this.subLines = [];
    this.startTime = Date.now() / 1000;
  }

  createMesh() {
    // Create material with custom shader for energy effect
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: this.params.color },
        intensity: { value: this.params.intensity },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 color;
        uniform float intensity;
        varying vec2 vUv;
        
        void main() {
          // Create flowing energy effect
          float flow = sin(vUv.x * 20.0 + time * 3.0) * 0.5 + 0.5;
          flow *= sin(vUv.y * 10.0 + time * 2.0) * 0.5 + 0.5;
          
          // Add pulsing glow
          float pulse = sin(time * 3.0) * 0.3 + 0.7;
          
          // Edge glow
          float edge = 1.0 - abs(vUv.y - 0.5) * 2.0;
          edge = pow(edge, 0.3);
          
          // Combine effects
          float alpha = flow * pulse * edge * intensity;
          
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      renderOrder: 999,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    return this.mesh;
  }

  generatePath(points) {
    if (!Array.isArray(points) || points.length < 2) return null;

    this.points = [];
    const totalPoints = points.length;
    const pointsPerSegment = Math.max(
      2,
      Math.floor(this.params.maxIterations / (totalPoints - 1))
    );

    // Generate points between each pair of input points
    for (let i = 0; i < totalPoints - 1; i++) {
      const start = points[i];
      const end = points[i + 1];

      // Generate points for this segment
      for (let j = 0; j <= pointsPerSegment; j++) {
        const t = j / pointsPerSegment;
        const pos = new THREE.Vector3().lerpVectors(start, end, t);

        // Add controlled randomness only to middle points of each segment
        if (j > 0 && j < pointsPerSegment) {
          const randomness = (1 - this.params.straightness) * 0.2;
          const perpX = -(end.z - start.z);
          const perpZ = end.x - start.x;
          const perpLength = Math.sqrt(perpX * perpX + perpZ * perpZ);
          if (perpLength > 0) {
            pos.x += (perpX / perpLength) * (Math.random() - 0.5) * randomness;
            pos.z += (perpZ / perpLength) * (Math.random() - 0.5) * randomness;
          }
          // Raise the line slightly above the crystals
          pos.y = 0.6 + Math.sin(t * Math.PI * 2) * 0.05;
        } else {
          // Keep start and end points slightly above crystals too
          pos.y = 0.6;
        }

        // Only add point if it's not the last point of the segment (except for the final segment)
        if (j < pointsPerSegment || i === totalPoints - 2) {
          this.points.push(pos);
        }
      }
    }

    // Create geometry using all points
    const curve = new THREE.CatmullRomCurve3(this.points);
    const tubeGeometry = new THREE.TubeGeometry(
      curve,
      this.points.length * 2,
      this.params.radius0,
      8,
      false
    );

    if (!this.material) {
      this.createMaterial();
    }

    if (this.mesh) {
      if (this.mesh.geometry) {
        this.mesh.geometry.dispose();
      }
      this.mesh.geometry = tubeGeometry;
    } else {
      this.mesh = new THREE.Mesh(tubeGeometry, this.material);
      // Ensure mesh is always rendered on top
      this.mesh.renderOrder = 999;
      this.mesh.material.depthTest = false;
    }

    this.geometry = tubeGeometry;
    return this.mesh;
  }

  createMaterial() {
    // Create material with custom shader for energy effect
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: this.params.color },
        intensity: { value: this.params.intensity },
        glowStrength: { value: 1.5 },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vPosition;
        void main() {
          vUv = uv;
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 color;
        uniform float intensity;
        uniform float glowStrength;
        varying vec2 vUv;
        varying vec3 vPosition;
        
        void main() {
          // Create flowing energy effect with improved variation
          float flow = sin(vUv.x * 20.0 + time * 3.0) * 0.5 + 0.5;
          flow *= sin(vUv.y * 10.0 + time * 2.0) * 0.5 + 0.5;
          
          // Enhanced pulsing glow
          float pulse = sin(time * 3.0) * 0.3 + 0.7;
          pulse *= sin(time * 1.5) * 0.15 + 0.85; // Secondary slower pulse
          
          // Improved edge glow with smoother falloff
          float edge = 1.0 - pow(abs(vUv.y - 0.5) * 2.0, 2.0);
          edge = pow(edge, 0.5);
          
          // Add sparkle effect
          float sparkle = pow(sin(vUv.x * 30.0 + time * 5.0) * 0.5 + 0.5, 4.0);
          sparkle *= pow(sin(vUv.y * 20.0 - time * 4.0) * 0.5 + 0.5, 4.0);
          
          // Combine all effects
          float alpha = (flow * pulse * edge + sparkle * 0.3) * intensity * glowStrength;
          
          // Add color variation
          vec3 finalColor = color * (0.8 + pulse * 0.4);
          finalColor += vec3(0.2, 0.2, 0.4) * sparkle; // Add blue-white sparkles
          
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      renderOrder: 999,
    });
    return this.material;
  }

  update(time) {
    if (this.material && this.mesh && this.mesh.geometry) {
      // Update time uniform for flowing effect
      this.material.uniforms.time.value = time * this.params.timeScale;

      // Animate radius and glow strength based on intensity
      const radiusScale = 1 + Math.sin(time * 3) * 0.2 * this.params.intensity;
      if (this.mesh.geometry.parameters) {
        this.mesh.geometry.parameters.radius =
          this.params.radius0 * radiusScale;
      }

      // Update intensity and glow strength
      this.material.uniforms.intensity.value = this.params.intensity;
      this.material.uniforms.glowStrength.value =
        1.5 + Math.sin(time * 2) * 0.3;
    }
  }

  setIntensity(value) {
    this.params.intensity = value;
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}
